import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  getVisaVendorAddotionalService,
  saveVisaVendorAddotionalService,
  createMultiVisaVendorAddotionalService,
  visaVendorUploadLoanScanCopy,
} from "../../../../services/visa/visa-additional-service";
import { useEffect, useState } from "react";
import { errorMsg } from "../../../../utils/toast-msg";
import { useNavigate, useParams } from "react-router-dom";
import { errorAlert } from "../../../../utils/error-alert";
import { successAlert } from "../../../../utils/success-alert";
import { CustomInput } from "../../../../components/CustomInput";
import { CustomMenuItem } from "../../../../components/CustomMenuItem";
import { getHajjPackage, saveHajjPackage } from "../../../../services/hajj/hajj-package";
import { CustomAutocomplete } from "../../../../components/CustomAutocomplete";
import { getAllVisaPackageOrders } from "../../../../services/visa/visa-package-order";
import { CustomSelect } from "../../../../components/CustomSelect";

const globalStyles = {
  fontFamily: "'Outfit', sans-serif",
};

const calculateTotalDays = (departureDate, returnDate) => {
  if (!departureDate || !returnDate) return "";

  const start = new Date(departureDate);
  const end = new Date(returnDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";
  if (end < start) return "";

  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to make it inclusive

  return diffDays;
};

const CreateHajjPackage = () => {
  const params = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (params.id === "new") {
          return;
        }

        const {
          data: { payload: newData },
        } = await getHajjPackage(params.id);
        setFormData(newData);
      } catch (error) {
        if (error?.response?.status === 403) {
          errorMsg("You have no permission to access this page");
        } else {
          console.error("An error occurred while fetching data:", error);
          errorMsg("An unexpected error occurred. Please try again later.");
        }
      }
    })();
  }, []);


  useEffect(() => {
    const totalDays = calculateTotalDays(
      formData.departureDate,
      formData.returnDate
    );

    if (totalDays !== formData.totalDays) {
      setFormData((prev) => ({ ...prev, totalDays }));
    }
  }, [formData.departureDate, formData.returnDate]);

  const handleChange = ({ target }) => {
    setFormData({ ...formData, [target.name]: target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const newData = { ...formData };
      const result = await saveHajjPackage(newData);

      if (result.error) return errorAlert(result.error.data.message);
      successAlert(
        `Hajj Package ${newData.id ? "Updated" : "Created"} Successfully`,
      );
      navigate("/dashboard/hajj-package-list");
    } catch (error) {
      errorAlert("Unable to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="placeholderColor"
      style={globalStyles}
      sx={{
        backgroundColor: "var(--color-bg-white)",
        padding: "16px",
        borderRadius: "12px",
        marginTop: { xs: "20px", sm: "0", md: "0" },
      }}
    >
      <Box sx={{ borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Box mt={2}>
            <Typography
              sx={{
                padding: "5px 0px",
                fontSize: "15px",
                fontWeight: 400,
                color: "var(--color-primary)",
                fontFamily: "Outfit",
              }}
            >
              {formData.id
                ? "Update Hajj Package"
                : "Create Hajj Package"}
            </Typography>
          </Box>
          <Grid container spacing={2} rowSpacing={3}>

            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                <span style={{ color: "red" }}>*</span> Package Name
              </Typography>
              <CustomInput
                required
                name="hajjPackageName"
                onChange={handleChange}
                placeholder="Enter Package Name"
                value={formData.hajjPackageName}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                Departure Date
              </Typography>

              <CustomInput
                type="date"
                name="departureDate"
                onChange={handleChange}
                placeholder="DD/MM/YYYY"
                value={formData.departureDate || ""}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                Return Date
              </Typography>
              <CustomInput
                name="returnDate"
                type="date"
                placeholder="DD/MM/YYYY"
                value={formData.returnDate || ""}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                Total Days
              </Typography>
              <CustomInput
                type="number"
                name="totalDays"
                placeholder="Auto calculated"
                value={formData.totalDays || ""}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                Total Persons
              </Typography>
              <CustomInput
                type="number"
                name="totalPerson"
                onChange={handleChange}
                placeholder="Enter Total Persons"
                value={formData.totalPerson}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                Status
              </Typography>
              <CustomSelect
                name="status"
                displayEmpty
                onChange={handleChange}
                value={formData.status || ""}
                renderValue={(selected) => selected || "Select Status"}
              >
                <CustomMenuItem value="">Select Status</CustomMenuItem>
                <CustomMenuItem value="In Process">In Process</CustomMenuItem>
                <CustomMenuItem value="Ongoing">Ongoing</CustomMenuItem>
                <CustomMenuItem value="Completed">Completed</CustomMenuItem>
              </CustomSelect>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "var(--color-gray)",
                  fontFamily: "Outfit",
                }}
              >
                Remarks
              </Typography>
              <CustomInput
                name="remarks"
                onChange={handleChange}
                placeholder="Enter Remarks"
                value={formData.remarks}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  fontFamily: "Outfit",
                  backgroundColor: "var(--color-primary-dark)",
                  color: "var(--color-bg-white)",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "var(--color-primary-dark)" },
                  minWidth: "120px",
                  height: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{ marginRight: "4px" }}
                    />
                    Loading...
                  </>
                ) : formData.id ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default CreateHajjPackage;
